import { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const perspectives = [
  {
    label: '专家视角',
    subtitle: '学术标准驱动',
    icon: 'ri-user-star-line',
    color: 'primary' as const,
    items: ['专题咬合度', '理论阐释深度', '专业完成度'],
  },
  {
    label: '学生视角',
    subtitle: '学习体验驱动',
    icon: 'ri-user-heart-line',
    color: 'accent' as const,
    items: ['理解程度', '课堂吸引力', '获得感与共鸣'],
  },
  {
    label: 'AI视角',
    subtitle: '模型融合驱动',
    icon: 'ri-brain-line',
    color: 'secondary' as const,
    items: ['内容评估模型', '感染力评估模型', '融合综合评价'],
  },
];

const cases = [
  {
    code: '本科08',
    title: '专家中部 · 学生尾部',
    desc: '专业结构尚可，但学习体验相对不足。专家认可内容深度，学生反馈获得感弱。',
    tag: '人工差异已核',
    highlight: '学生体验落差',
  },
  {
    code: '高职03',
    title: '专家头部 · 学生尾部',
    desc: '差距显著：专家高度认可专题完成度，学生体验未同步跟上。高职样本小，仅作个案说明。',
    tag: '高职样本说明',
    highlight: '显著脱节',
  },
  {
    code: '高职08',
    title: '学生侧相对靠前',
    desc: '与高职03方向相反：哪些课堂元素更易进入学生体验？学生视角给出了不同答案。',
    tag: '人工差异已核',
    highlight: '学生视角领先',
  },
];

export default function P36TriView({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.03);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.03);
  const [activeCase, setActiveCase] = useState<number | null>(null);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 背景光晕 */}
      <div ref={orb1Ref} className="absolute top-16 right-20 w-96 h-96 rounded-full bg-primary-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-16 w-80 h-80 rounded-full bg-accent-100/6 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-14">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-[11px] font-body font-semibold mb-3 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
            <i className="ri-scan-line text-xs" />
            技术点七
          </span>
          <h2 className={`font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 mb-3 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            指标映射与<span className="text-primary-600">专家、学生、AI三方视角</span>
          </h2>
          <p className={`text-sm md:text-base text-foreground-600 font-body max-w-2xl mx-auto transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            同一堂课，三种评价体系会给出不同判断——差异不是错误，而是互补
          </p>
        </div>

        {/* 核心洞察卡片 */}
        <div className={`max-w-xl mx-auto mb-8 md:mb-10 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-4 p-5 rounded-2xl bg-accent-50/60 border border-accent-200/40">
            <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-xl text-accent-600" />
            </div>
            <div>
              <p className="text-sm font-body font-bold text-foreground-800">核心洞察</p>
              <p className="text-xs text-foreground-600 font-body mt-0.5">三方差异 ≠ 某一方错误，而是评价体系本身存在不同价值取向</p>
            </div>
          </div>
        </div>

        {/* 三方视角 — 三列大卡片 */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto mb-8 md:mb-10 transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {perspectives.map((p, i) => {
            const colorMap = {
              primary: { bg: 'bg-primary-50', border: 'border-primary-200/40', text: 'text-primary-600', badge: 'bg-primary-100 text-primary-700', ring: 'ring-primary-200/30' },
              accent: { bg: 'bg-accent-50', border: 'border-accent-200/40', text: 'text-accent-600', badge: 'bg-accent-100 text-accent-700', ring: 'ring-accent-200/30' },
              secondary: { bg: 'bg-secondary-50', border: 'border-secondary-200/40', text: 'text-secondary-600', badge: 'bg-secondary-100 text-secondary-700', ring: 'ring-secondary-200/30' },
            };
            const c = colorMap[p.color];
            return (
              <div
                key={p.label}
                className={`p-5 rounded-2xl bg-background-50 border ${c.border} transition-all duration-500 hover:ring-2 ${c.ring}`}
                style={{ transitionDelay: `${500 + i * 100}ms` }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl ${c.bg} ${c.border} border flex items-center justify-center`}>
                    <i className={`${p.icon} text-lg ${c.text}`} />
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-800">{p.label}</h3>
                    <span className={`inline-block text-[10px] font-body font-semibold px-2 py-0.5 rounded-full ${c.badge}`}>{p.subtitle}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {p.items.map((item) => (
                    <div key={item} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-100/60">
                      <i className={`ri-checkbox-circle-line text-xs ${c.text}`} />
                      <span className="text-xs text-foreground-700 font-body">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* 分隔线 */}
        <div className="max-w-5xl mx-auto mb-8 md:mb-10">
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-background-200/60" />
            <span className="text-xs font-body text-foreground-400 whitespace-nowrap">实际课堂验证案例</span>
            <div className="flex-1 h-px bg-background-200/60" />
          </div>
        </div>

        {/* 案例对比 — 可点击展开 */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto mb-8 transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {cases.map((c, i) => (
            <div
              key={c.code}
              className={`p-5 rounded-2xl border transition-all duration-500 cursor-pointer ${
                activeCase === i
                  ? 'bg-background-50 border-primary-300/50 ring-2 ring-primary-200/40'
                  : 'bg-background-50 border-background-200/60 hover:border-primary-200/40'
              }`}
              style={{ transitionDelay: `${800 + i * 100}ms` }}
              onClick={() => setActiveCase(activeCase === i ? null : i)}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-heading text-lg font-bold text-foreground-800">{c.code}</span>
                <span className="text-[10px] font-body text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">{c.tag}</span>
              </div>
              <div className="mb-3">
                <span className="inline-block text-xs font-body font-bold text-primary-700 bg-primary-50 px-2.5 py-1 rounded-lg">
                  {c.highlight}
                </span>
              </div>
              <p className="text-xs font-body font-semibold text-foreground-700 mb-2">{c.title}</p>
              <p className="text-[11px] text-foreground-500 font-body leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>

        {/* 展开详情区 */}
        <div className={`max-w-5xl mx-auto transition-all duration-500 ${activeCase !== null ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none h-0 overflow-hidden'}`}>
          {activeCase !== null && (
            <div className="p-5 rounded-2xl bg-background-100/50 border border-background-200/60">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-bar-chart-box-line text-sm text-primary-600" />
                </div>
                <div>
                  <p className="text-sm font-body font-bold text-foreground-800 mb-1">
                    {cases[activeCase].code} 深度分析
                  </p>
                  <p className="text-xs text-foreground-600 font-body leading-relaxed">
                    {cases[activeCase].code === '本科08' && '本科课程中，专家更看重理论体系的完整性与学术深度，而学生则更在意内容是否贴近自身经验、互动是否充分。两方评价出现"中部-尾部"落差，说明课堂在专业性与亲和力之间存在结构性张力。'}
                    {cases[activeCase].code === '高职03' && '高职样本中专家与学生的评价方向完全相反：专家认可专题完成度，学生却给出较低评价。这提示高职课堂需要更关注实践关联与互动体验，而非仅满足专家设定的专业标准。'}
                    {cases[activeCase].code === '高职08' && '高职08课程中学生评价相对靠前，说明某些教学设计元素（如案例贴近度、互动频次）更容易被学生感知和认可。这为"什么样的课更容易打动学生"提供了实证线索。'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 底部提示 */}
        <div className={`max-w-3xl mx-auto text-center mt-8 transition-all duration-1000 delay-1000 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/60">
            <i className="ri-mic-line text-sm text-foreground-400" />
            <span className="text-xs font-body text-foreground-500">口播表达建议：采用"头部、中部、尾部"的相对位置描述，不报精确名次</span>
          </div>
        </div>
      </div>
    </div>
  );
}